import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import { Save, Warehouse as WarehouseIcon } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useCan } from '@rbac/useCan';
import PageHeader from '@/components/PageHeader';
import StatTile from '@/components/StatTile';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { useAppToast } from '@/lib/toast';
import { errorMessage, number } from '@/lib/format';
import { EXPIRY_STATES, expiryMeta } from '@/lib/expiry';
import Panel from '@/components/Panel';

/**
 * The warehouse, and the two numbers that define what "expiring soon" means.
 *
 * Those thresholds are settings rather than constants because the answer is
 * genuinely different per business: a bakery's warning window is a couple of
 * days, a pharmacy's is six months. Changing them here re-colours every screen
 * in the app, which is exactly why the preview below the fields shows what the
 * current stock looks like under the policy being edited.
 */
export default function WarehouseSettings() {
  const { fetchApi } = useAppApi();
  const toast = useAppToast();
  const can = useCan();
  const editable = can('warehouse', 'update');

  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchApi('warehouseSummary');
      const row = (res && res.data) || null;
      setSummary(row);
      if (row) {
        setForm({
          name: row.name || '',
          code: row.code || '',
          address: row.address || '',
          city: row.city || '',
          state: row.state || '',
          manager_name: row.manager_name || '',
          contact_number: row.contact_number || '',
          critical_days: row.critical_days,
          warning_days: row.warning_days,
        });
      }
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not load the warehouse'));
    } finally {
      setLoading(false);
    }
    // fetchApi identity changes each render in the library hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((x) => ({ ...x, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const critical = Number(form.critical_days);
    const warning = Number(form.warning_days);
    const errors = {};
    if (!form.name.trim()) errors.name = 'A warehouse name is required';
    if (!Number.isInteger(critical) || critical < 0 || critical > 365) errors.critical_days = 'Between 0 and 365 days';
    if (!Number.isInteger(warning) || warning < 0 || warning > 365) errors.warning_days = 'Between 0 and 365 days';
    if (!errors.critical_days && !errors.warning_days && critical > warning) {
      errors.critical_days = 'The critical window cannot be longer than the warning window';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setSaving(true);
    try {
      await fetchApi('crud', {
        model: 'Warehouse',
        action: 'update',
        where: { id: summary.id },
        data: { ...form, critical_days: critical, warning_days: warning },
      });
      toast.success('Warehouse updated', 'Every screen now uses the new expiry policy.');
      load();
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not save the warehouse'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState rows={6} />;
  if (err && !form) return <ErrorState message={err} onRetry={load} />;
  if (!summary || !form) {
    return (
      <EmptyState
        icon={<WarehouseIcon size={40} />}
        title="No warehouse set up"
        message="Run `npm run seed:demo` to create the warehouse and its starting stock."
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Warehouse"
        subtitle="Its details, and the policy that decides what counts as expiring soon."
      />

      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <StatTile label="Products" value={number(summary.total_products)} />
        <StatTile label="Units in stock" value={number(Math.round(summary.total_quantity))} />
        {EXPIRY_STATES.filter((s) => s !== 'watch' && s !== 'unknown').map((s) => (
          <StatTile key={s} label={expiryMeta(s).short} value={number(summary.counts[s])} meta={expiryMeta(s)} />
        ))}
      </div>

      {err ? (
        <Alert variant="error" title="Could not save" dismissible onDismiss={() => setErr('')}>
          {err}
        </Alert>
      ) : null}

      <form onSubmit={submit} className="space-y-4">
        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Expiry policy</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Critical window (days)"
              type="number"
              min="0"
              max="365"
              value={form.critical_days}
              onChange={set('critical_days')}
              error={fieldErrors.critical_days}
              disabled={!editable}
            />
            <Input
              label="Warning window (days)"
              type="number"
              min="0"
              max="365"
              value={form.warning_days}
              onChange={set('warning_days')}
              error={fieldErrors.warning_days}
              disabled={!editable}
            />
          </div>
            <p className="text-xs text-[var(--app-muted)] ">
              A batch is flagged <span className="font-semibold" style={{ color: 'var(--exp-critical-fg)' }}>red</span> inside the
              critical window and <span className="font-semibold" style={{ color: 'var(--exp-warning-fg)' }}>amber</span> inside
              the warning window. Anything up to three times the warning window is shown as on the horizon.
            </p>
          <div className="flex flex-wrap gap-2 rounded-xl bg-[var(--app-surface-2)] p-3 text-xs">
            {EXPIRY_STATES.map((s) => {
              const m = expiryMeta(s);
              return (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold"
                  style={{ color: m.fg, background: m.bg, borderColor: m.border }}
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: m.solid }} />
                  {m.label}
                  <span className="nums opacity-70">{number(summary.counts[s])}</span>
                </span>
              );
            })}
          </div>
        </Panel>

        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Name" value={form.name} onChange={set('name')} error={fieldErrors.name} disabled={!editable} required />
            <Input label="Code" value={form.code} onChange={set('code')} disabled={!editable} />
            <Input label="Address" value={form.address} onChange={set('address')} disabled={!editable} />
            <Input label="City" value={form.city} onChange={set('city')} disabled={!editable} />
            <Input label="State" value={form.state} onChange={set('state')} disabled={!editable} />
            <Input label="Manager" value={form.manager_name} onChange={set('manager_name')} disabled={!editable} />
            <Input label="Contact number" value={form.contact_number} onChange={set('contact_number')} disabled={!editable} />
          </div>
        </Panel>

        {editable ? (
          <div className="flex justify-end">
            <Button type="submit" loading={saving} leftIcon={<Save size={16} />}>
              Save warehouse
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[var(--app-muted)]">You have read-only access to the warehouse settings.</p>
        )}
      </form>
    </div>
  );
}
